import { Form, FormInstance, Upload, message } from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import { ReactComponent as RemoveImg } from 'assets/icons/x-circle-fill.svg';
import CloudArrowUpImage from 'assets/images/CloudArrowUp.svg';
import { ImageComponent } from 'presentation/components/ImageComponent';
import { showConfirm } from 'presentation/redux/features/app/appSlice';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import getMessage from 'utilities/constants/MESSAGE';
import { commonHelpers } from 'utilities/helpers';
import { FILE_UPLOAD } from '../../../../../infrastructure/config/CONSTANTS';

interface props {
    title: string;
    initialImageUrl?: string | undefined;
    form?: FormInstance;
    getBinary?: any;
    existedFile?: any;
    imageId?: number;
    required?: boolean;
    name: string;
    editName: string;
    onChange?: (value?: any) => void;
}
const STATUS = {
    UPLOADING: 'uploading',
    DONE: 'done',
    UNDER_CONSTRUCTION: '施工中',
};

const UploadImage = ({
    title,
    getBinary,
    initialImageUrl,
    form,
    existedFile,
    imageId,
    name,
    required,
    editName,
    ...props
}: props) => {
    const [imageUrl, setImageUrl] = useState<string>();
    const [initialImg, setInitialImg] = useState<string>();
    const [imageIds, setImageIds] = useState<number>();
    const [isRequired, setIsRequired] = useState<boolean>(false);
    const [fileName, setFileName] = useState<string>('');
    const [isPdfFile, setIsPdfFile] = useState<boolean>(false);
    const dispatch = useDispatch();
    const fileTypes = FILE_UPLOAD.FILE_TYPE_CU_005_01_02?.split(',');
    const maxFileSize = Number(FILE_UPLOAD.FILE_MAX_SIZE_CU_005_01_02);
    useEffect(() => {
        setIsRequired(required ?? false);
    }, [required]);

    useEffect(() => {
        if (initialImageUrl) setInitialImg(initialImageUrl);
        if (existedFile && existedFile.file && existedFile.file.originFileObj) {
            const file = existedFile.file.originFileObj as RcFile;
            setFileName(file.name || '');
            setIsPdfFile(
                file.name?.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf',
            );
            getBase64(file, (url) => setImageUrl(url));
        }
    }, []);

    useEffect(() => {
        setInitialImg(initialImageUrl);
        setImageIds(imageId);
        if (initialImageUrl) {
            const isPdfUrl =
                /\.pdf(\?|$)/i.test(initialImageUrl) ||
                initialImageUrl.startsWith('data:application/pdf');
            const isImageUrl =
                initialImageUrl.startsWith('data:image/') ||
                /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(initialImageUrl);
            if (isPdfUrl || !isImageUrl) {
                setIsPdfFile(true);
                const m = initialImageUrl.match(/([^/?#]+\.pdf)(\?|$)/i);
                setFileName(m ? m[1] : 'PDF');
            }
        }
    }, [initialImageUrl]);

    const getBase64 = (img: RcFile, callback: (url: string) => void) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => callback(reader.result as string));
        reader.readAsDataURL(img);
    };

    const acceptFile = (fileType: string) => {
        let accepted = false;
        fileTypes?.forEach((item) => {
            if (fileType == item) {
                accepted = true;
            } else if (item.match('([a-z])*/\\*')) {
                const regex = new RegExp(`${item}([a-z])*`);
                if (fileType.match(regex)) {
                    accepted = true;
                }
            }
        });
        return accepted;
    };

    const beforeUpload = (file: RcFile) => {
        const invalidFileType = commonHelpers.validateFileTypes(file, fileTypes);
        if (invalidFileType) {
            message.error(getMessage('M007', invalidFileType));
        }
        const isValidFileSize = commonHelpers.validateFileSize(file, maxFileSize);
        if (!isValidFileSize) {
            message.error(getMessage('M006', maxFileSize + 'MB'));
        }
        return !invalidFileType && isValidFileSize;
    };

    const dummyRequest = ({ onSuccess }: any) => {
        setTimeout(() => {
            onSuccess('ok');
        }, 0);
    };

    const onChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
        if (info.file.status === STATUS.UPLOADING) {
            return;
        }
        if (info.file.status === STATUS.DONE) {
            const file = info.file.originFileObj as RcFile;
            setFileName(file.name || info.file.name || '');
            setIsPdfFile(
                file.name?.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf',
            );
            getBase64(file, (url) => {
                if (getBinary) getBinary(info.file.originFileObj);
                setImageUrl(url);
                if (form && imageIds) {
                    form.setFieldValue(editName, [imageIds]);
                }
            });
        }
    };

    const showDeleteConfirm = () => {
        const deleteProps = commonHelpers.getConfirmProps(
            'N0900',
            {
                onOk: () => {
                    removeImage();
                },
            },
            '削除',
        );
        dispatch(showConfirm(deleteProps));
    };

    const removeImage = () => {
        setImageUrl(undefined);
        setInitialImg(undefined);
        setFileName('');
        setIsPdfFile(false);
        if (form) {
            form.setFieldValue(name, undefined);
            if (imageIds) {
                form.setFieldValue(editName, [imageIds]);
            }
            props?.onChange?.();
        }
    };

    return (
        <>
            <section
                style={{ display: 'flex', justifyContent: 'space-between' }}
                className="label-form"
            ></section>
            <Form.Item name={name} className="image-upload" required={isRequired}>
                <Upload
                    className="upload-wrap-custom button-style"
                    name={name}
                    showUploadList={false}
                    beforeUpload={beforeUpload}
                    onChange={onChange}
                    customRequest={dummyRequest}
                    style={{ display: 'flex' }}
                    accept={FILE_UPLOAD.FILE_TYPE_CU_005_01_02}
                >
                    {imageUrl || initialImg ? (
                        isPdfFile && (fileName || imageUrl || initialImg) ? (
                            <div
                                id="image-component"
                                className="flex items-center delete-image"
                                style={{
                                    width: '100%',
                                    height: 217.28,
                                    border: '1px solid #D0D3D8',
                                    borderRadius: 4,
                                    position: 'relative',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#F5F6F8',
                                }}
                            >
                                <div className="remove-icon">
                                    <RemoveImg
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            showDeleteConfirm();
                                        }}
                                        fill="red"
                                    />
                                </div>
                                <span
                                    style={{
                                        color: '#192F51',
                                        fontSize: 16,
                                        fontWeight: 700,
                                        wordBreak: 'break-word',
                                        textAlign: 'center',
                                    }}
                                >
                                    {fileName || 'PDF'}
                                </span>
                            </div>
                        ) : (
                            <ImageComponent
                                style={{ width: 343, height: 217.28 }}
                                src={imageUrl || initialImg || ''}
                                parentClassName="w-full"
                                alt="avatar"
                                clickRemove={showDeleteConfirm}
                            />
                        )
                    ) : (
                        <section
                            style={{
                                padding: '53px 26px',
                                border: '1px solid #D0D3D8',
                                borderRadius: 4,
                                background: '#ffffff',
                                flex: 1,
                            }}
                        >
                            <div className="center-inside" style={{ width: '100%' }}>
                                <img alt="" src={CloudArrowUpImage} style={{ margin: 'auto' }} />
                            </div>
                            <div className="upload-title" style={{ marginTop: 12 }}>
                                {title}
                            </div>
                        </section>
                    )}
                </Upload>
            </Form.Item>
        </>
    );
};

export default UploadImage;
